import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Modal, Table, Input, Button, Empty } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS — same organization/role markers as
// DrawerOrganizationInformation / DrawerRoleInformation, so this wizard's
// two steps read as the same concepts everywhere else in the app.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingCircleCheck, faUserShield } from "@fortawesome/free-solid-svg-icons";
// CUSTOM HOOKS
import useGetAllOrganizationsLight from "../../../customHooks/GET_requests/useGetAllOrganizationsLight";
import useGetAllRolesLight from "../../../customHooks/GET_requests/useGetAllRolesLight";
// COMPONENTS
import TableColumnOrganizationType from "../table/TableColumnOrganizationType";
// MASTER-ORG ROLE MARKERS — same star/shield pair used by the Roles table.
import { isMasterOrgId } from "../../../utils/masterOrganization";
import MasterRoleStar from "../../../components/roles/MasterRoleStar";
import TenantRoleShield from "../../../components/roles/TenantRoleShield";
// INTERFACES
import { OganizationLightInterface } from "../../../interfaces/Organizations.interface";
import { RoleLightInterface } from "../../../interfaces/Roles.interface";
import { SelectOrganizationAndRoleInterface } from "../../../interfaces/Global.interface";
// FLATBUFFER ENUMS
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";

interface OrganizationRow {
  value: string;
  label: string;
  type?: OrgType;
}

interface RoleRow {
  value: string;
  label: string;
  organizationId: string;
  isMaster: boolean;
}

type Step = "organization" | "role";
type OrgSortField = "label" | "type";

const normalizeId = (idValue: unknown): string => {
  if (typeof idValue === "string" || typeof idValue === "number") return String(idValue);
  if (!idValue) return "";
  if (typeof idValue === "object") return formatUuidT(idValue as any) || String(idValue);
  return String(idValue);
};

/**
 * Master-admin header switcher: one Button ("<org> / <role>") that walks a
 * 2-step wizard inside a single Modal instead of two independent
 * pickers (the previous TagSelectOrganizationsLight + TagSelectRolesLight
 * pair). Step 1 picks an organization — not applied yet. Its footer's
 * primary button is "Select role" instead of "Save", and moves to step 2:
 * the role picker, scoped to whichever organization was just picked (not the
 * currently-applied one) and showing ONLY the roles assigned to it — no
 * cross-org grouping. Save there commits BOTH the organization and the role
 * together — cancelling at any point discards both drafts.
 */
function TagSelectOrganizationRoleLight({
  value,
  roleValue,
  roleLabel,
  onSave,
  disabledInput,
  withBanks,
}: SelectOrganizationAndRoleInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<Step>("organization");
  const [searchText, setSearchText] = useState("");
  const [draftOrgValue, setDraftOrgValue] = useState<string | undefined>(undefined);
  const [draftRoleValue, setDraftRoleValue] = useState<string | undefined>(undefined);
  const [orgSort, setOrgSort] = useState<{ field?: OrgSortField; order?: "ascend" | "descend" }>(
    {},
  );

  // ************************************************ */
  // CUSTOM HOOKS — ORGANIZATION (step 1) ************ */
  const { organizationsLight: organizations, isLoadingGetAllOrganizationsLight } =
    useGetAllOrganizationsLight(withBanks);

  const orgOptions: OrganizationRow[] = organizations.map((item: OganizationLightInterface) => ({
    value: formatUuidT(item.id),
    label: String(item.name ?? ""),
    type: item.type,
  }));

  const orgValueExists = value && orgOptions.some((opt) => opt.value === value);
  const displayOrgValue = orgValueExists ? value : undefined;
  const selectedOrgLabel = orgOptions.find((opt) => opt.value === displayOrgValue)?.label;
  const draftOrgLabel = orgOptions.find((opt) => opt.value === draftOrgValue)?.label;

  // LiftNet (type MASTER) is always item 0 — under whichever column the
  // operator sorted, only the non-master rows reorder; the sort direction
  // never moves LiftNet out of the top slot.
  const typeLabelForSort = (type?: OrgType) => (type !== undefined ? orgTypeLabel(type) : "");
  const sortedOrgOptions: OrganizationRow[] = useMemo(() => {
    const filtered = orgOptions.filter((opt) =>
      opt.label.toLowerCase().includes(searchText.trim().toLowerCase()),
    );
    const master = filtered.filter((opt) => opt.type === OrgType.MASTER);
    const rest = filtered.filter((opt) => opt.type !== OrgType.MASTER);
    if (orgSort.field && orgSort.order) {
      const direction = orgSort.order === "ascend" ? 1 : -1;
      const compare =
        orgSort.field === "type"
          ? (a: OrganizationRow, b: OrganizationRow) =>
              direction * typeLabelForSort(a.type).localeCompare(typeLabelForSort(b.type))
          : (a: OrganizationRow, b: OrganizationRow) => direction * a.label.localeCompare(b.label);
      rest.sort(compare);
    }
    return [...master, ...rest];
  }, [orgOptions, searchText, orgSort]);

  // ************************************************ */
  // CUSTOM HOOKS — ROLE (step 2) ******************** */
  // Scoped to the organization just picked in step 1 (the DRAFT), never the
  // currently-applied globalOrganizationId — that's the whole point of the
  // 2-step flow: preview roles for where you're ABOUT to move, not where
  // you are. Empty until a draft org exists (useGetAllRolesLight skips it).
  const { rolesLight: roles, isLoadingGetAllRolesLight } = useGetAllRolesLight({
    orgId: draftOrgValue ?? "",
  });

  const allRoles: RoleRow[] = useMemo(
    () =>
      roles.map((role: RoleLightInterface) => ({
        value: normalizeId(role.id),
        label: String(role.name ?? ""),
        organizationId: normalizeId(role.organization?.id),
        isMaster: isMasterOrgId(normalizeId(role.organization?.id)),
      })),
    [roles],
  );

  // `/roles/light/:orgId` can come back with roles from more than the
  // queried org (e.g. inherited/global ones) — the role step must only ever
  // list roles actually assigned to the org just picked, for ANY org
  // (including LiftNet itself), never a wider tree.
  const rolesForSelectedOrg: RoleRow[] = useMemo(
    () => allRoles.filter((role) => role.organizationId === draftOrgValue),
    [allRoles, draftOrgValue],
  );

  const selectedRoleValue = normalizeId(roleValue);
  const roleValueExists = allRoles.some((opt) => opt.value === selectedRoleValue);
  const displayRoleValue = roleValueExists ? selectedRoleValue : undefined;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const openModal = () => {
    if (disabledInput) return;
    setStep("organization");
    setDraftOrgValue(displayOrgValue);
    setDraftRoleValue(undefined);
    setSearchText("");
    setIsModalOpen(true);
  };

  const onCancel = () => setIsModalOpen(false);

  const goToRoleStep = () => {
    if (!draftOrgValue) return;
    // A role previewed for a different org is never valid here — start from
    // nothing unless the org draft is still the currently-applied one, in
    // which case preselect the role that's actually active today.
    setDraftRoleValue(draftOrgValue === displayOrgValue ? displayRoleValue : undefined);
    setStep("role");
  };

  const goBackToOrganizationStep = () => {
    setStep("organization");
  };

  const onSaveBoth = () => {
    if (!draftOrgValue || !draftRoleValue) return;
    const orgRecord = orgOptions.find((opt) => opt.value === draftOrgValue);
    const roleRecord = roles.find(
      (role: RoleLightInterface) => normalizeId(role.id) === draftRoleValue,
    );
    onSave(draftOrgValue, orgRecord, draftRoleValue, roleRecord);
    setIsModalOpen(false);
  };

  const onOrgTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<OrganizationRow> | SorterResult<OrganizationRow>[],
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    setOrgSort({
      field: single?.field as OrgSortField | undefined,
      order: single?.order ?? undefined,
    });
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (draftRoleValue && !allRoles.some((opt) => opt.value === draftRoleValue)) {
      setDraftRoleValue(undefined);
    }
  }, [allRoles]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const orgColumns: ColumnsType<OrganizationRow> = [
    {
      title: t("report.selectOrganization") || "Organization",
      dataIndex: "label",
      key: "label",
      sorter: true,
      sortOrder: orgSort.field === "label" ? orgSort.order : undefined,
    },
    {
      title: t("organizationManagement.type") || "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      sorter: true,
      sortOrder: orgSort.field === "type" ? orgSort.order : undefined,
      render: (type?: OrgType) =>
        type !== undefined ? <TableColumnOrganizationType type={orgTypeLabel(type)} /> : "--",
    },
  ];

  const roleColumns: ColumnsType<RoleRow> = [
    {
      title: t("header.roles") || "Role",
      dataIndex: "label",
      key: "label",
      render: (label: string, row: RoleRow) => (
        <>
          <MasterRoleStar show={row.isMaster} />
          <TenantRoleShield show={!row.isMaster} />
          {label}
        </>
      ),
    },
  ];

  const headerButtonLabel = `${selectedOrgLabel || "--"} / ${roleLabel || "--"}`;

  return (
    <>
      <Tooltip title={headerButtonLabel}>
        <Button
          size="small"
          className="buttonStyle__1 generalStyles__width200px generalStyles__textEllipsis"
          disabled={disabledInput}
          onClick={openModal}
        >
          {headerButtonLabel}
        </Button>
      </Tooltip>
      <Modal
        title={
          <div className="component__tagSelectLight__header">
            <span className="component__tagSelectLight__headerBadge">
              <FontAwesomeIcon
                icon={step === "organization" ? faBuildingCircleCheck : faUserShield}
              />
            </span>
            <span className="component__tagSelectLight__headerText">
              {step === "organization" ? (
                t("header.selectOrganizationTitle") || "Select organization"
              ) : (
                <>
                  {t("header.selectRoleTitle") || "Select role"}
                  {draftOrgLabel && (
                    <span className="component__tagSelectLight__headerTextMuted">
                      {` / ${draftOrgLabel}`}
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={onCancel}
        width={560}
        destroyOnClose
        footer={
          <div className="component__tagSelectLight__footer">
            <Button className="component__tagSelectLight__footerButton" onClick={onCancel}>
              {t("general.cancel")}
            </Button>
            {step === "role" && (
              <Button
                className="component__tagSelectLight__footerButton"
                onClick={goBackToOrganizationStep}
              >
                {t("general.back")}
              </Button>
            )}
            {step === "organization" ? (
              <Button
                type="primary"
                className="component__tagSelectLight__footerButton"
                disabled={!draftOrgValue}
                onClick={goToRoleStep}
              >
                {t("header.selectRoleTitle") || "Select role"}
              </Button>
            ) : (
              <Button
                type="primary"
                className="component__tagSelectLight__footerButton"
                disabled={!draftRoleValue}
                onClick={onSaveBoth}
              >
                {t("general.save")}
              </Button>
            )}
          </div>
        }
      >
        {/* Fixed height, same in both steps: an org search bar vs. nothing,
            or a Table vs. an Empty state, would otherwise render at
            different heights and the modal would visibly resize as the
            operator moves between "Select organization" and "Select role". */}
        <div className="component__tagSelectLight__body">
          {step === "organization" ? (
            <div className="component__tagSelectLight__orgStep">
              <p className="component__tagSelectLight__description">
                {t("header.selectOrganizationDescription")}
              </p>
              <Input.Search
                allowClear
                placeholder={t("general.search") || "Search"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="component__tagSelectLight__search"
              />
              {/* Fills whatever's left below the description/search (flex:1
                  on this wrapper), then scroll.y="95%" bounds the Table's
                  OWN scroll region to that — leaving 5% slack against
                  rounding so `__body` above never overflows too. Two
                  scrollbars (the Table's internal one AND `__body`'s) is
                  the "double scroll" a fixed-px scroll.y estimate risked. */}
              <div className="component__tagSelectLight__orgTableWrap">
                <Table<OrganizationRow>
                  size="small"
                  rowKey="value"
                  style={{ height: "100%" }}
                  loading={isLoadingGetAllOrganizationsLight}
                  columns={orgColumns}
                  dataSource={sortedOrgOptions}
                  pagination={false}
                  scroll={{ y: "95%" }}
                  onChange={onOrgTableChange}
                  rowSelection={{
                    type: "radio",
                    selectedRowKeys: draftOrgValue ? [draftOrgValue] : [],
                    onChange: (keys) => setDraftOrgValue(keys[0] as string),
                  }}
                  onRow={(record) => ({
                    onClick: () => setDraftOrgValue(record.value),
                    className: "generalStyles__hoverCursor",
                  })}
                />
              </div>
            </div>
          ) : (
            <>
              <p className="component__tagSelectLight__description">
                {draftOrgLabel
                  ? `${t("header.selectRoleDescription")} (${draftOrgLabel})`
                  : t("header.selectRoleDescription")}
              </p>
              {/* No search bar in this step (spec) — the empty state renders
                  INSIDE the table (via `locale`) rather than swapping the
                  table out for an <Empty/>, so the body keeps the same
                  height whether the org has roles or not. No `scroll.y`
                  here either: with few roles the table should just render
                  at its natural height, not reserve an always-visible
                  scroll track — the surrounding `__body` wrapper is the one
                  that scrolls, and only once the org actually has enough
                  roles to overflow it. */}
              <Table<RoleRow>
                size="small"
                showHeader={false}
                rowKey="value"
                loading={isLoadingGetAllRolesLight}
                columns={roleColumns}
                dataSource={rolesForSelectedOrg}
                pagination={false}
                locale={{ emptyText: <Empty description={t("general.noData")} /> }}
                rowSelection={{
                  type: "radio",
                  selectedRowKeys: draftRoleValue ? [draftRoleValue] : [],
                  onChange: (keys) => setDraftRoleValue(keys[0] as string),
                }}
                onRow={(record) => ({
                  onClick: () => setDraftRoleValue(record.value),
                  className: "generalStyles__hoverCursor",
                })}
              />
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

export default TagSelectOrganizationRoleLight;
